import { useState } from 'react';
import { $t } from 'hooks/i18n';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Title } from 'components/Title';

export default function DebitdepositPage() {
	
	const [pageReady, setPageReady] = useState(true);
	    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [amortList, setAmortList] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    // ---------------------------------------------------------
    // FORMAT DATE
    // ---------------------------------------------------------
    const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    // ---------------------------------------------------------
    // LOAD AMORTIZATION
    // ---------------------------------------------------------
    const handleLoad = async () => {
        if (!dateFrom || !dateTo) {
            alert('Please select both From Date and To Date.');
            return;
        }
        if (dateFrom > dateTo) {
            alert('From Date cannot be greater than To Date.');
            return;
        }
        setLoading(true);
        setResult(null);
        setAmortList([]);
        setSelectedRows([]);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_PATH}mandate/drequest-list`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        dateFrom: formatDate(dateFrom),
                        dateTo: formatDate(dateTo)
                    })
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(
                    data.message ||
                    `HTTP error! Status: ${response.status}`
                );
            }
            const list = data.data || [];
            setAmortList(list);
            if (list.length === 0) {
                setResult(
                    'No pending amortization records found for the selected date range.'
                );
            }
        }
        catch (error) {
            console.error(
                'Error loading amortization:',
                error
            );
            setResult(
                'Error: ' + error.message
            );
            setAmortList([]);
            setSelectedRows([]);
        }
        finally {
            setLoading(false);
        }
    };
    // ---------------------------------------------------------
    // SUBMIT SELECTED RECORDS
    // ---------------------------------------------------------
    const handleSubmit = async () => {
        if (!selectedRows || selectedRows.length === 0) {
            alert('Please select at least one amortization record.');
            return;
        }
        const confirmSubmit = window.confirm(
            `Submit ${selectedRows.length} selected debit request(s) to Paynimo?`
        );
        if (!confirmSubmit) {
            return;
        }
        setSubmitting(true);
        setResult(null);
        try {
            // Only send primary keys to backend
            const collectionIds = selectedRows.map(
                row => row.collectionid
            );
            const response = await fetch(
                `${process.env.REACT_APP_API_PATH}mandate/send-drequest`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        collectionIds: collectionIds
                    })
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(
                    data.message ||
                    `HTTP error! Status: ${response.status}`
                );
            }
            const successCount = data.count || 0;
            const failedCount = data.failedCount || 0;
            setResult(
                `${data.message} Success: ${successCount}, Failed: ${failedCount}`
            );
            // Remove successfully processed records
            const processedIds = (data.results || [])
                .filter(row => row.status !== 'Error')
                .map(row => row.collectionid);
            setAmortList(prev =>
                prev.filter(
                    row => !processedIds.includes(row.collectionid)
                )
            );
            setSelectedRows([]);
        }
        catch (error) {
            console.error(
                'Error submitting debit requests:',
                error
            );
            setResult(
                'Error: ' + error.message
            );
        }
        finally {
            setSubmitting(false);
        }
    };
    // ---------------------------------------------------------
    // DATE DISPLAY
    // ---------------------------------------------------------
    const dateTemplate = (rowData) => {
        if (!rowData.paydate) {
            return '';
        }
        const date = new Date(rowData.paydate);
        if (isNaN(date.getTime())) {
            return rowData.paydate;
        }
        return date.toLocaleDateString('en-IN');
    };
    // ---------------------------------------------------------
    // AMOUNT DISPLAY
    // ---------------------------------------------------------
    const amountTemplate = (rowData) => {
        const amount = Number(rowData.amount || 0);
        return amount.toLocaleString(
            'en-IN',
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        );
    };
    // ---------------------------------------------------------
    // STATUS DISPLAY
    // ---------------------------------------------------------
    const statusTemplate = (rowData) => {
        if (!rowData.req_status) {
            return (
                <span className="text-green-600 font-semibold">
                    Pending
                </span>
            );
        }
        return (
            <span className="text-red-600 font-semibold">
                {rowData.req_status}
            </span>
        );
    };
    // ---------------------------------------------------------
    // TOTAL SELECTED AMOUNT
    // ---------------------------------------------------------
    const selectedAmount = selectedRows.reduce(
        (total, row) =>
            total + Number(row.amount || 0),
        0
    );
	return (
		<main id="DebitdepositPage" className="main-page">
<section className="page-section q-pa-md" >
    <div className="container-fluid">
        <div className="grid ">
            <div className="col comp-grid" >
                <Title title={$t('debitdeposit')}   titleClass="text-lg font-bold text-primary" subTitleClass="text-500"      separator={false} />
                <div className="">
                    <section className="page-section q-pa-md">
                        <div className="container-fluid">
                            <div className="grid">
                                <div className="col-12">
                                    <Title
                                    title={$t('debitdeposit')}
                                    titleClass="text-lg font-bold text-primary"
                                    subTitleClass="text-500"
                                    separator={false}
                                    />
                                    <div className="p-4 bg-white shadow-lg rounded-lg mt-3">
                                        <h2 className="text-xl font-bold mb-4">
                                        Debit Presentation by Date Range
                                        </h2>
                                        {/* ================================================= */}
                                        {/* DATE FILTER */}
                                        {/* ================================================= */}
                                        <div className="grid">
                                            <div className="col-12 md:col-4">
                                                <label className="block mb-2 font-semibold">
                                                Date From
                                                </label>
                                                <Calendar
                                                showIcon
                                                showButtonBar
                                                dateFormat="yy-mm-dd"
                                                className="w-full"
                                                selectionMode="single"
                                                value={dateFrom}
                                                onChange={(e) =>
                                                setDateFrom(e.value)
                                                }
                                                placeholder={$t('Date From')}
                                                />
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <label className="block mb-2 font-semibold">
                                                Date To
                                                </label>
                                                <Calendar
                                                showIcon
                                                showButtonBar
                                                dateFormat="yy-mm-dd"
                                                className="w-full"
                                                selectionMode="single"
                                                value={dateTo}
                                                onChange={(e) =>
                                                setDateTo(e.value)
                                                }
                                                placeholder={$t('Date To')}
                                                />
                                            </div>
                                            <div className="col-12 md:col-2 flex align-items-end">
                                                <Button
                                                label={
                                                loading
                                                ? 'Loading...'
                                                : 'Load Amortization'
                                                }
                                                icon="pi pi-search"
                                                className="w-full"
                                                disabled={
                                                loading ||
                                                submitting
                                                }
                                                onClick={handleLoad}
                                                />
                                            </div>
                                            <div className="col-12 md:col-2 flex align-items-end">
                                                <Button
                                                label={
                                                submitting
                                                ? 'Submitting...'
                                                : `Debits Submit (${selectedRows.length})`
                                                }
                                                icon="pi pi-send"
                                                severity="success"
                                                className="w-full"
                                                disabled={
                                                submitting ||
                                                loading ||
                                                selectedRows.length === 0
                                                }
                                                onClick={handleSubmit}
                                                />
                                            </div>
                                        </div>
                                        {/* ================================================= */}
                                        {/* MESSAGE */}
                                        {/* ================================================= */}
                                        {result && (
                                        <div className="mt-3 p-3 bg-gray-100 border-round text-center font-semibold">
                                            {result}
                                        </div>
                                        )}
                                        {/* ================================================= */}
                                        {/* SUMMARY */}
                                        {/* ================================================= */}
                                        {amortList.length > 0 && (
                                        <div className="grid mt-4">
                                            <div className="col-12 md:col-4">
                                                <div className="p-3 bg-blue-50 border-round">
                                                    <div className="text-500">
                                                        Total Records
                                                    </div>
                                                    <div className="text-2xl font-bold">
                                                        {amortList.length}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <div className="p-3 bg-green-50 border-round">
                                                    <div className="text-500">
                                                        Selected Records
                                                    </div>
                                                    <div className="text-2xl font-bold">
                                                        {selectedRows.length}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 md:col-4">
                                                <div className="p-3 bg-yellow-50 border-round">
                                                    <div className="text-500">
                                                        Selected Amount
                                                    </div>
                                                    <div className="text-2xl font-bold">
                                                        ₹
                                                        {selectedAmount.toLocaleString(
                                                        'en-IN',
                                                        {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                        }
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        )}
                                        {/* ================================================= */}
                                        {/* AMORTIZATION TABLE */}
                                        {/* ================================================= */}
                                        {amortList.length > 0 && (
                                        <div className="mt-4">
                                            <DataTable
                                                value={amortList}
                                                selection={selectedRows}
                                                onSelectionChange={(e) =>
                                                setSelectedRows(
                                                e.value || []
                                                )
                                                }
                                                dataKey="collectionid"
                                                paginator
                                                rows={10}
                                                rowsPerPageOptions={[
                                                10,
                                                25,
                                                50,
                                                100
                                                ]}
                                                responsiveLayout="scroll"
                                                stripedRows
                                                showGridlines
                                                size="small"
                                                emptyMessage="No amortization records found."
                                                >
                                                {/* CHECKBOX */}
                                                <Column
                                                    selectionMode="multiple"
                                                    headerStyle={{
                                                    width: '3rem'
                                                    }}
                                                    />
                                                    {/* PRIMARY KEY */}
                                                    <Column
                                                        field="collectionid"
                                                        header="Collection ID"
                                                        sortable
                                                        />
                                                        {/* POLICY */}
                                                        <Column
                                                            field="policyno"
                                                            header="Policy No"
                                                            sortable
                                                            />
                                                            <Column
                                                                field="customername"
                                                                header="Customer Name"
                                                                sortable
                                                                />
                                                                <Column
                                                                    field="period"
                                                                    header="EMI#"
                                                                    body={dateTemplate}
                                                                    sortable
                                                                    />
                                                                    {/* PAY DATE */}
                                                                    <Column
                                                                        field="paydate"
                                                                        header="Pay Date"
                                                                        body={dateTemplate}
                                                                        sortable
                                                                        />
                                                                        {/* AMOUNT */}
                                                                        <Column
                                                                            field="amount"
                                                                            header="Amount"
                                                                            body={amountTemplate}
                                                                            sortable
                                                                            style={{
                                                                            textAlign: 'right'
                                                                            }}
                                                                            />
                                                                            {/* MERCHANT REF */}
                                                                            <Column
                                                                                field="merchanttrxnrefid"
                                                                                header="Merchant Ref"
                                                                                />
                                                                                {/* STATUS */}
                                                                                <Column
                                                                                    field="req_status"
                                                                                    header="Status"
                                                                                    body={statusTemplate}
                                                                                    />
                                                                                </DataTable>
                                                                            </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </section>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
		</main>
	);
}
